import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isSupported, solveChallenge } from './powSolver';

/**
 * Invisible bot protection for public forms.
 *
 * Fetches a signed proof-of-work challenge, solves it in a background worker
 * and hands the solution to the surrounding form. The user does not have to do
 * anything - by the time a form is filled in, the check has long finished.
 *
 * Deliberately dependency free apart from react and prop-types so the same file
 * can be used in a formik form and in a server rendered form:
 *
 *   formik      <HumanCheck endpoint="..." onSolved={(v) => formik.setFieldValue("human_check", v)} />
 *   plain form  <HumanCheck endpoint="..." fieldName="human_check" />
 *
 * All user facing strings come in through `labels` so the host application can
 * use whatever translation mechanism it has.
 */

export const STATUS = {
  LOADING: 'loading',
  SOLVING: 'solving',
  SOLVED: 'solved',
  FAILED: 'failed',
  UNSUPPORTED: 'unsupported',
};

const DEFAULT_LABELS = {
  solving: 'Sicherheitsprüfung läuft …',
  solved: 'Sicherheitsprüfung abgeschlossen',
  failed: 'Sicherheitsprüfung fehlgeschlagen.',
  retry: 'Erneut versuchen',
  unsupported:
    'Die Sicherheitsprüfung ist in diesem Browser nicht verfügbar. Bitte öffnen Sie die Seite über eine verschlüsselte Verbindung (https) und verwenden Sie einen aktuellen Browser.',
};

// Fetch a fresh challenge this long before the current one expires, so a form
// that takes a while to fill in never fails on submit.
const REFRESH_MARGIN_SECONDS = 60;

// A refresh that fails while the current solution is still valid is retried
// this often, rather than taking a working form away from the user.
const RETRY_AFTER_FAILED_REFRESH_SECONDS = 30;

const HumanCheck = ({
  endpoint,
  fieldName,
  labels,
  onSolved,
  onStatusChange,
  refreshKey,
  refreshEvent,
}) => {
  const [status, setStatus] = useState(STATUS.LOADING);
  const [payload, setPayload] = useState('');

  // Kept in refs so the effect does not restart when the parent re-renders with
  // a new inline callback or label object.
  const onSolvedRef = useRef(onSolved);
  const onStatusChangeRef = useRef(onStatusChange);
  onSolvedRef.current = onSolved;
  onStatusChangeRef.current = onStatusChange;

  const text = { ...DEFAULT_LABELS, ...(labels || {}) };

  // What is currently in the surrounding form, and until when it is usable.
  const solvedRef = useRef({ payload: '', expires: 0 });

  const publish = useCallback((nextStatus, nextPayload, expires) => {
    setStatus(nextStatus);
    setPayload(nextPayload || '');
    solvedRef.current = { payload: nextPayload || '', expires: expires || 0 };
    if (onStatusChangeRef.current) onStatusChangeRef.current(nextStatus);
    if (onSolvedRef.current) onSolvedRef.current(nextPayload || '');
  }, []);

  const [attempt, setAttempt] = useState(0);

  // Set when the re-run comes from the expiry timer or the retry button, where
  // the solution we already hold may still be good. A re-run caused by
  // refreshKey means the server rejected that solution, so it must be dropped.
  const keepOnFailureRef = useRef(false);
  const retry = useCallback(() => {
    keepOnFailureRef.current = true;
    setAttempt((value) => value + 1);
  }, []);

  // Drops the current solution and fetches a fresh challenge. Used when the
  // server has rejected what the form is holding.
  const rearm = useCallback(() => {
    keepOnFailureRef.current = false;
    setAttempt((value) => value + 1);
  }, []);

  // For forms that are not driven by React: the server's error response can
  // dispatch this event to re-arm the widget, because a solution is single use
  // and the one in the form has just been spent.
  useEffect(() => {
    if (!refreshEvent || typeof window === 'undefined') return undefined;

    window.addEventListener(refreshEvent, rearm);
    return () => window.removeEventListener(refreshEvent, rearm);
  }, [refreshEvent, rearm]);

  useEffect(() => {
    if (!isSupported()) {
      publish(STATUS.UNSUPPORTED, '');
      return undefined;
    }

    let disposed = false;
    let solver = null;
    let refreshTimer = null;

    const solution = solvedRef.current;
    const keepOnFailure =
      keepOnFailureRef.current &&
      solution.payload !== '' &&
      solution.expires > Math.floor(Date.now() / 1000);
    keepOnFailureRef.current = false;

    if (!keepOnFailure && solution.payload !== '') {
      // A re-run that the timer did not ask for means the server rejected what
      // the form is holding. Drop it now rather than leaving a spent solution
      // in place for the moment it takes to solve the replacement.
      publish(STATUS.LOADING, '');
    } else {
      setStatus(STATUS.LOADING);
    }

    // Handing the old solution back lets the server keep the original start
    // time of this form session, so the minimum fill-in time is not restarted
    // by a re-armed challenge.
    const url = solution.payload
      ? `${endpoint}${endpoint.indexOf('?') === -1 ? '?' : '&'}previous=${encodeURIComponent(
          solution.payload
        )}`
      : endpoint;

    fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`challenge_request_failed_${response.status}`);
        return response.json();
      })
      .then((challenge) => {
        if (disposed) return;
        setStatus(STATUS.SOLVING);

        solver = solveChallenge(challenge);
        return solver.promise.then((solved) => {
          if (disposed) return;
          publish(STATUS.SOLVED, solved.payload, challenge.expires);

          if (challenge.expires) {
            const secondsLeft =
              challenge.expires -
              Math.floor(Date.now() / 1000) -
              REFRESH_MARGIN_SECONDS;
            refreshTimer = setTimeout(retry, Math.max(secondsLeft, 30) * 1000);
          }
        });
      })
      .catch((error) => {
        if (disposed) return;

        if (keepOnFailure) {
          // The refresh failed but what the form holds is still valid. Say
          // nothing, keep the form submittable, and try again shortly.
          setStatus(STATUS.SOLVED);
          refreshTimer = setTimeout(
            retry,
            RETRY_AFTER_FAILED_REFRESH_SECONDS * 1000
          );
          return;
        }

        publish(
          error && error.message === 'unsupported'
            ? STATUS.UNSUPPORTED
            : STATUS.FAILED,
          ''
        );
      });

    return () => {
      disposed = true;
      if (solver) solver.cancel();
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, [endpoint, attempt, refreshKey, publish, retry]);

  return (
    <div className="human-check" data-status={status}>
      {fieldName && (
        <input type="hidden" name={fieldName} value={payload} readOnly />
      )}

      {status === STATUS.LOADING || status === STATUS.SOLVING ? (
        <span className="human-check__status" aria-live="polite">
          <span
            className="spinner-border spinner-border-sm me-2"
            aria-hidden="true"
          />
          {text.solving}
        </span>
      ) : null}

      {status === STATUS.SOLVED && (
        <span
          className="human-check__status human-check__status--solved"
          aria-live="polite"
        >
          ✓ {text.solved}
        </span>
      )}

      {status === STATUS.FAILED && (
        <span
          className="human-check__status human-check__status--failed"
          role="alert"
        >
          {text.failed}{' '}
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={retry}
          >
            {text.retry}
          </button>
        </span>
      )}

      {status === STATUS.UNSUPPORTED && (
        <span
          className="human-check__status human-check__status--failed"
          role="alert"
        >
          {text.unsupported}
        </span>
      )}
    </div>
  );
};

HumanCheck.defaultProps = {
  fieldName: null,
  labels: null,
  onSolved: null,
  onStatusChange: null,
  refreshKey: 0,
  refreshEvent: null,
};

HumanCheck.propTypes = {
  /** URL that returns a signed challenge as JSON. */
  endpoint: PropTypes.string.isRequired,
  /** Renders a hidden input under this name - for forms that are not React. */
  fieldName: PropTypes.string,
  labels: PropTypes.shape({
    solving: PropTypes.string,
    solved: PropTypes.string,
    failed: PropTypes.string,
    retry: PropTypes.string,
    unsupported: PropTypes.string,
  }),
  /** Called with the base64 payload, and with "" whenever it becomes invalid. */
  onSolved: PropTypes.func,
  onStatusChange: PropTypes.func,
  /**
   * Change this to discard the current solution and fetch a fresh challenge -
   * the server accepts each solution exactly once, so the surrounding form has
   * to re-arm the widget after a rejected submit.
   */
  refreshKey: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Name of a window event that re-arms the widget, for forms that submit
   * without React and therefore cannot bump refreshKey.
   */
  refreshEvent: PropTypes.string,
};

export default HumanCheck;
